import { useCallback, useEffect } from "react";
import { AdState, OrcaWindowEvents, VideoEvents } from "../../globals";
import { VideoEvent } from "../../lib/analytics/utils";
import { addOrcaEvent } from "../utils";
import { useVideoDataContext } from "../contexts/VideoDataContext";
import { useVideoElementRefContext } from "../contexts/VideoElementRefContext";
import { useVideoAnalyticsContext } from "../contexts/VideoAnalyticsContext";
import { useAutoplayContext } from "../contexts/AutoplayContext";
import { useStickyVideoContext } from "../contexts/StickyVideoContext";

import { usePlayerState } from "./useVideoControls/usePlayerState";

const useVideoControls = ({
  config,
  isNativeIOSPlayer,
  adState,
  updateAutoplayState,
  toggleSubsHandler = () => {},
  toggleMuteHandler = () => {}
}) => {
  const videoElementRef = useVideoElementRefContext();
  const { videoId, uniqueId } = useVideoDataContext();
  const { autoplayState, isMotionReduced } = useAutoplayContext();
  const { sendAnalyticsEvent } = useVideoAnalyticsContext();
  const { isStickyEnabled, isOpenSticky, playingId, updateStickyContext } =
    useStickyVideoContext();

  const { debug = false } = config;

  const { playerState, updatePlayerState } = usePlayerState(config);

  const setVideoMuteValue = useCallback(
    value => {
      addOrcaEvent(OrcaWindowEvents.MUTE, value, videoId, debug);
      videoElementRef.current.muted = value;
      sendAnalyticsEvent(value ? VideoEvent.MUTE : VideoEvent.UNMUTE);
    },
    [videoElementRef, debug, videoId, sendAnalyticsEvent]
  );

  const getVideoMuteValue = useCallback(
    () => videoElementRef?.current?.muted,
    [videoElementRef]
  );

  useEffect(() => {
    const videoElement = videoElementRef.current;
    if (!videoElement) return;

    // Keep the video element in sync with the player state.
    if (playerState.isMuted) setVideoMuteValue(true);
  }, [videoElementRef.current]);

  // Called when user clicks custom play btns
  const onPlayButton = () => {
    playerState.isPlaying ? pause() : play();
    updatePlayerState({ isAutoplaying: false });
    sendAnalyticsEvent(
      playerState.isPlaying ? VideoEvent.PAUSE : VideoEvent.RESUME
    );
    // Update sticky context to record user clicking pause
    if (
      playerState.isPlaying &&
      isStickyEnabled &&
      !isOpenSticky &&
      playingId === uniqueId
    )
      updateStickyContext({ playingId: null });
  };

  const play = useCallback(() => {
    videoElementRef.current.play();
  }, [videoElementRef]);

  const autoplay = useCallback(() => {
    play();
    updatePlayerState({
      isAutoplaying: true
    });
  }, [play, updatePlayerState]);

  const pause = useCallback(() => {
    videoElementRef.current.pause();
  }, [videoElementRef]);

  // video html element callback
  const handlePlayEvent = isPlaying => {
    // Hack to stop iris.stop() from autoplaying video when a user picks a new vid in a playlist
    if (adState === AdState.REQUESTED) {
      videoElementRef.current.pause();
    }

    updatePlayerState({ isPlaying });

    // catch play/pause event from native controls
    if (isNativeIOSPlayer && videoElementRef.current?.currentTime != 0) {
      sendAnalyticsEvent(isPlaying ? VideoEvent.RESUME : VideoEvent.PAUSE);
    }
  };

  const reset = () => {
    pause();
    videoElementRef.current.currentTime = 0;
    updatePlayerState({
      isStarted: false,
      isPlaying: false
    });
  };

  // Toggle subtitles on and off.
  const setShowSubtitles = useCallback(
    value => {
      toggleSubsHandler(value);

      updatePlayerState({
        showSubtitles: value
      });

      if (value) {
        addOrcaEvent(OrcaWindowEvents.SUBS_ENABLED, value, videoId, debug);
      } else {
        addOrcaEvent(OrcaWindowEvents.SUBS_DISABLED, value, videoId, debug);
      }

      // Update the localStorage with the user preference.
      if (typeof window !== "undefined") {
        localStorage.setItem("userSubtitlesPreference", value);
      }
    },
    [updatePlayerState, videoId, debug, toggleSubsHandler]
  );

  const resetControlsState = useCallback(() => {
    if (!autoplayState.isLooping) {
      videoElementRef.current.currentTime = 0;
      updatePlayerState({
        isMuted: getVideoMuteValue(),
        isStarted: false,
        isPlaying: false
      });
    }
  }, [autoplayState.isLooping, videoElementRef, getVideoMuteValue]);

  /**
   * Cleanup logic for when the component using the useVideoControl hook unmounts.
   * Properly disposes of the video element for when the component is destroyed to prevent memory leaks.
   */
  useEffect(() => {
    const videoElement = videoElementRef.current;

    return () => {
      videoElement.pause();
      videoElement.src = "";
      videoElement.load();
    };
  }, []);

  const setMuteState = useCallback(
    mute => {
      setVideoMuteValue(mute);
      updatePlayerState({ isMuted: mute });

      const muteEvent = new Event(mute ? VideoEvents.MUTE : VideoEvents.UNMUTE);
      videoElementRef.current.dispatchEvent(muteEvent);

      toggleMuteHandler(mute);
    },
    [setVideoMuteValue, updatePlayerState, videoElementRef, toggleMuteHandler]
  );

  /**
   * Toggle the mute state of the video.
   */
  const toggleMute = useCallback(() => {
    setMuteState(!playerState.isMuted);
  }, [playerState.isMuted, setMuteState]);

  /**
   * This function handles the volume change from the audio slider.
   * @param {volume} volume The volume to update to coming from the audio slider.
   */
  const handleVolumeChange = volume => {
    videoElementRef.current.volume = volume;
    updatePlayerState({
      volume
    });
  };

  // If html media muted changes, update playerState (ex picture in picture)
  useEffect(() => {
    updatePlayerState({
      isMuted: getVideoMuteValue()
    });
  }, [videoElementRef?.current?.muted]);

  useEffect(() => {
    if (autoplayState.isLooping) {
      videoElementRef.current?.setAttribute("loop", "");
    }
  }, [autoplayState.isLooping]);

  const handleLivePreview = useCallback(() => {
    pause();
    toggleMute();
    updateAutoplayState({ isLivePreview: false, isAutoplay: false });
    setShowSubtitles(true);
  }, [pause, setShowSubtitles, toggleMute, updateAutoplayState]);

  // when another sticky media element is played, pause the video
  useEffect(() => {
    if (
      playingId &&
      playingId !== uniqueId &&
      playerState.isPlaying &&
      isStickyEnabled
    ) {
      pause();
    }
  }, [playingId, uniqueId, playerState.isPlaying, pause, isStickyEnabled]);

  const setInitialMuteState = useCallback(
    forcePlayAd => {
      const initialMuteValue = getInitialMuteValue(
        config,
        autoplayState,
        forcePlayAd
      );
      setMuteState(initialMuteValue);
    },
    [autoplayState, config, setMuteState]
  );

  const handleReduceMotion = useCallback(
    reduceMotion => {
      if (reduceMotion && playerState.isAutoplaying) {
        pause();
      } else if (!reduceMotion && playerState.isAutoplaying) {
        autoplay();
      }
    },
    [playerState.isAutoplaying, pause, autoplay]
  );

  useEffect(() => {
    handleReduceMotion(isMotionReduced);
  }, [isMotionReduced, handleReduceMotion]);

  return {
    playerState,
    onPlayButton,
    play,
    pause,
    reset,
    toggleMute,
    handleVolumeChange,
    setShowSubtitles,
    handlePlayEvent,
    resetControlsState,
    handleLivePreview,
    setInitialMuteState,
    autoplay
  };
};

export default useVideoControls;

const getInitialMuteValue = (config, autoplayState, forcePlayAd) => {
  if (typeof window === "undefined") return true;

  const isAutoplayMute =
    autoplayState.isAutoplay &&
    !forcePlayAd &&
    !autoplayState.overrideAutoplayMuteBehavior;
  if (isAutoplayMute) return true;

  const isMutedOverride = config.isMutedOverride;

  return isMutedOverride ?? false;
};
